import type { FunctionComponent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { FormProvider, useForm } from 'react-hook-form'
import { Button, Grid, GridItem, PageTitle, TypographyV2 as Typography } from '@which/seatbelt'
import { dynamicGa4DataLayerPush } from '@which/shared'

import { useKindeAuth } from '@kinde-oss/kinde-auth-react'

import type { Meta } from '../../../generated/frontend'
import { useForgotPasswordMutation } from '../../../generated/frontend'
import { isLocal } from '../../../shared'
import { ErrorComponent } from '../../../shared/components/Error'
import { Link } from '../../../shared/components/Link'
import { useBaseMetaTags } from '../../../shared/hooks/useBaseMetaTags'
import { paywallUrls } from '../../../shared/paywall-urls'
import { FormFields } from '../components/FormFields'
import { QuestionAccordion } from '../components/QuestionAccordion'
import { ForgotPasswordEmailField, Links } from '../data'
import { accountPagesDataLayer, setTransactionName } from '../utils'
import styles from './ForgotPasswordPage.module.scss'

enum PromptTypes {
  none = 'none',
  create = 'create',
  login = 'login',
}
const ForgotPasswordPage: FunctionComponent = () => {
  const [emailInput, setEmailInput] = useState('')

  const { getBaseMetaTags } = useBaseMetaTags()
  const methods = useForm({ mode: 'onBlur' })
  const { handleSubmit } = methods
  const paywallState = useMemo(paywallUrls, [])
  const kinde = useKindeAuth()

  const [forgotPasswordMutation, { error, loading, called }] = useForgotPasswordMutation()

  const getMetaTags = ({ meta, imageUrl, twitterCard }: ForgotPasswordMetaArgs) => {
    return [...getBaseMetaTags({ meta, imageUrl, twitterCard })]
  }

  setTransactionName('account/forgotPassword')

  const metaTagObj = {
    canonical: 'https://www.which.co.uk/forgot-password',
    title: 'Forgotten password | Can’t login to which.co.uk',
    fbAppId: '320949621749168',
    desc: "Enter the email address associated with your account and we'll send you a verification code to reset your password.",
  }

  useEffect(() => {
    const kindeLogin = () => {
      const domain = isLocal() ? 'localhost' : '.which.co.uk'

      if (!kinde.isAuthenticated) {
        document.cookie = `sessionId=temp;path=/;domain=${domain};expires=Fri, 31 Dec 9999 23:59:59 GMT`
        document.cookie = `blaize_session=temp;path=/;domain=${domain};expires=Fri, 31 Dec 9999 23:59:59 GMT`
      }
      sessionStorage.setItem('kindeRedirect', '/')

      kinde.login({
        loginHint: emailInput,
        prompt: PromptTypes.login,
        connectionId: paywallState.kindeConnectionId,
      })
    }
    if (called && !loading) {
      kindeLogin()
    }
  }, [called, emailInput, kinde, loading, paywallState.kindeConnectionId])

  const needHelpContent = (
    <Typography textStyle="sb-text-body-default-regular">
      If you’re still having problems resetting your password please visit our{' '}
      <Link href={Links.helpCenter}>help centre.</Link>
    </Typography>
  )

  const handleOnSubmit = (formObj) => {
    dynamicGa4DataLayerPush({
      event: 'click_button',
      item_text: 'Reset my password',
    })

    setEmailInput(formObj.email)
    forgotPasswordMutation({ variables: { email: formObj.email } })
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  return (
    <>
      <Helmet>
        {getMetaTags({
          meta: metaTagObj,
          imageUrl: 'https://www.which.co.uk/forgot-password',
          twitterCard: 'summary_large_image',
        })}
        <script>
          {accountPagesDataLayer({
            content_type: 'Forgot Password',
          })}
        </script>
      </Helmet>
      <Grid className={styles.forgotPasswordPage}>
        <GridItem span={{ medium: 6, large: 4 }} columnStart={{ medium: 4, large: 5 }}>
          <PageTitle pageTitle="Reset your password" pageTitleTag="h1" />
          <Typography
            tag="p"
            textStyle="sb-text-body-default-regular"
            className={styles.forgotPasswordPageTitleCopy}
          >
            Enter the email address associated with your account and we’ll send you a verification
            code to reset your password.
          </Typography>
          <FormProvider {...methods}>
            <form method="post">
              <FormFields fields={ForgotPasswordEmailField} />
              <div className={styles.forgotPasswordPageFormFooter}>
                <Button
                  data-testid="forgot-password-button"
                  enableSpinner={loading}
                  onClick={handleSubmit((formObj, e) => {
                    e?.preventDefault()
                    handleOnSubmit(formObj)
                  })}
                  className={styles.forgotPasswordPageSubmit}
                >
                  Continue
                </Button>
                <div className={styles.forgotPasswordPageReturnUrl}>
                  <Link
                    appearance="primary"
                    href={Links.returnUrl}
                    textStyle="sb-text-body-default-regular"
                    data-which-id="link"
                    className={styles.forgotPasswordPageReturnUrlLink}
                  >
                    Cancel and return to login
                  </Link>
                </div>
              </div>
            </form>
          </FormProvider>
          <QuestionAccordion
            label="Need help?"
            content={needHelpContent}
            calledFrom="forgotPassword"
          />
        </GridItem>
      </Grid>
    </>
  )
}

export default ForgotPasswordPage

/////// IMPLEMENTATION /////////

type ForgotPasswordMetaArgs = {
  meta: Partial<Meta>
  imageUrl: string
  twitterCard: string
}
